<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-1">
      <span class="text-h6 mr-1">PowerBI Variance</span>
      <v-menu open-on-hover offset-y bottom :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <v-icon small color="info" v-bind="attrs" v-on="on">
            mdi-information-outline
          </v-icon>
        </template>
        <v-card max-width="360" class="pa-3">
          <div class="info-title mb-2">How counts are matched</div>
          <div class="info-row mb-2">
            <v-icon small color="info" class="mt-1 mr-2">mdi-file-document-outline</v-icon>
            <div>
              <div class="info-label">DA count</div>
              <div class="info-text">DeliveryAdvice.movement_date</div>
            </div>
          </div>
          <div class="info-row">
            <v-icon small color="info" class="mt-1 mr-2">mdi-truck-outline</v-icon>
            <div>
              <div class="info-label">Trip count</div>
              <div class="info-text">
                Leg.movement_date (not Trip.trip_date) &mdash; the PowerBI
                trips field is derived per-leg.
              </div>
            </div>
          </div>
        </v-card>
      </v-menu>
    </div>
    <div class="mb-4 subtitle">
      Checks DA and Trip counts between the main system and PowerBI for a
      given date range. Trip count is checked by the movement date of its
      Leg.
    </div>

    <v-row>
      <v-col cols="12" sm="4" md="3">
        <v-menu
          v-model="fromDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="fromDate"
              label="From Date"
              readonly
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="fromDate" @input="fromDateMenu = false" />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="4" md="3">
        <v-menu
          v-model="toDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="toDate"
              label="To Date"
              readonly
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="toDate" @input="toDateMenu = false" />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="4" md="3" class="d-flex align-center">
        <v-btn
          color="primary"
          :loading="checking"
          :disabled="!fromDate || !toDate"
          @click="checkVariance"
        >
          Check
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="errorMessage" type="error" class="mt-4" dense text>
      {{ errorMessage }}
    </v-alert>

    <template v-if="result">
      <v-row class="mt-2">
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="section-title">DA Count</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span>Main (DeliveryAdvice)</span>
                <strong>{{ result.da.main_count }}</strong>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span>Power BI (DeliveryAdviceDetails)</span>
                <strong>{{ result.da.power_bi_count }}</strong>
              </div>
              <div
                class="d-flex justify-space-between"
                :class="result.da.missing_count ? 'error--text' : 'success--text'"
              >
                <span>Missing</span>
                <strong>{{ result.da.missing_count }}</strong>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="section-title">Trip Count</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span>Main (Leg.movement_date &rarr; Trip)</span>
                <strong>{{ result.trip.main_count }}</strong>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span>Power BI (DeliveryAdviceDetails)</span>
                <strong>{{ result.trip.power_bi_count }}</strong>
              </div>
              <div
                class="d-flex justify-space-between"
                :class="result.trip.missing_count ? 'error--text' : 'success--text'"
              >
                <span>Missing</span>
                <strong>{{ result.trip.missing_count }}</strong>
              </div>
              <div class="caption mt-2">
                A trip whose only legs are subhire ones is never written to
                Power BI by design - expected here, not a real gap.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="result.da.missing_count || result.trip.missing_count" class="mt-2">
        <v-col cols="12" md="6" v-if="result.da.missing_count">
          <div class="d-flex align-center mb-2">
            <div class="section-title">
              Missing DA Numbers ({{ result.da.missing_count }})
            </div>
            <v-btn
              x-small
              text
              color="primary"
              class="ml-2"
              @click="copyMissing(result.da.missing_items)"
            >
              <v-icon x-small left>mdi-content-copy</v-icon>
              Copy
            </v-btn>
          </div>
          <div class="chip-box">
            <v-chip
              v-for="item in result.da.missing_items"
              :key="item.id"
              small
              label
              class="ma-1"
              color="error"
              outlined
              :to="{ name: 'app-admin-delivery-advice-details-id', params: { id: item.id } }"
              target="_blank"
            >
              {{ item.display }}
              <v-icon x-small right>mdi-open-in-new</v-icon>
            </v-chip>
          </div>
        </v-col>
        <v-col cols="12" md="6" v-if="result.trip.missing_count">
          <div class="d-flex align-center mb-2">
            <div class="section-title">
              Missing Trip IDs ({{ result.trip.missing_count }})
            </div>
            <v-btn
              x-small
              text
              color="primary"
              class="ml-2"
              @click="copyMissing(result.trip.missing_items)"
            >
              <v-icon x-small left>mdi-content-copy</v-icon>
              Copy
            </v-btn>
          </div>
          <div class="chip-box">
            <v-chip
              v-for="item in result.trip.missing_items"
              :key="item.id"
              small
              label
              class="ma-1"
              color="error"
              outlined
              :to="{ name: 'app-admin-trip-details-id', params: { id: item.id } }"
              target="_blank"
            >
              {{ item.display }}
              <v-icon x-small right>mdi-open-in-new</v-icon>
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { isAuthenticated, hasRouteAccess } from "@/utils/functions";

export default {
  data() {
    return {
      fromDate: null,
      toDate: null,
      fromDateMenu: false,
      toDateMenu: false,
      checking: false,
      errorMessage: "",
      result: null,
    };
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    }
  },
  methods: {
    checkVariance() {
      this.checking = true;
      this.errorMessage = "";
      this.result = null;
      this.$api.powerbiVariance
        .check({ from_date: this.fromDate, to_date: this.toDate })
        .then((res) => {
          this.result = res.data;
        })
        .catch((err) => {
          this.errorMessage =
            (err && err.detail) || "Failed to fetch variance data.";
        })
        .finally(() => {
          this.checking = false;
        });
    },
    copyMissing(items) {
      const text = items.map((item) => item.display).join(", ");
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Copied to clipboard",
            color: "success",
          });
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Failed to copy",
            color: "error",
          });
        });
    },
  },
};
</script>

<style scoped>
.subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
}
.chip-box {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 4px;
}
.info-title {
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.8);
}
.info-row {
  display: flex;
  align-items: flex-start;
}
.info-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
}
.info-text {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
}
</style>
